import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useMemo, useState, type FormEvent } from 'react';
import { isBookLanguageCode } from '../../../utils/globals';
import BookLanguageListForm, { type BookLanguageDraft } from './BookLanguageListForm';
import SettingsCrudDialog from './SettingsCrudDialog';
import SettingsListDeleteButton from './SettingsListDeleteButton';
import SettingsListEditButton from './SettingsListEditButton';
import SettingsListSection, { SettingsEmptyTableRow } from './SettingsListSection';
import { createBookLanguage, updateBookLanguage } from './bookLanguageApi';
import type { BookLanguageRow } from './singleTableListSupport';
import {
  BOOK_LANGUAGE_DUPLICATE_CODE_MESSAGE,
  bookLanguageDraftFilename,
  bookLanguageFlagTableSrc,
  bookLanguageRowIsActive,
  formatSettingsRowDate,
  settingsListApiError,
} from './singleTableListSupport';
import { useBookLanguageList } from './useBookLanguageList';

const headSx = { fontWeight: 600, bgcolor: 'action.hover' } as const;

function BookLanguageFlagCell({ row }: { row: BookLanguageRow }) {
  const localSrc = bookLanguageFlagTableSrc(row);
  if (localSrc) {
    return (
      <Box
        component="img"
        src={localSrc}
        alt=""
        sx={{
          width: 32,
          height: 20,
          objectFit: 'cover',
          borderRadius: 0.5,
          border: 1,
          borderColor: 'divider',
          display: 'block',
        }}
      />
    );
  }
  const legacy = row.flag_url?.trim();
  if (legacy) {
    return (
      <Link href={legacy} target="_blank" rel="noopener noreferrer" color="primary">
        харах
      </Link>
    );
  }
  return <>—</>;
}

export default function BookLanguageList() {
  const { rows, loading, error, load, deleteById, deletingId } = useBookLanguageList<BookLanguageRow>();
  const existingLanguageIds = useMemo(() => new Set(rows.map((r) => r.id)), [rows]);
  const [draft, setDraft] = useState<BookLanguageDraft | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const openCreate = () => {
    setFormError(null);
    setDraft({ mode: 'create', id: '', name: '', filename: '', active: true });
  };

  const openEdit = (row: BookLanguageRow) => {
    setFormError(null);
    setDraft({
      mode: 'edit',
      id: row.id,
      name: row.name,
      filename: bookLanguageDraftFilename(row),
      active: bookLanguageRowIsActive(row),
    });
  };

  const closeForm = () => {
    setDraft(null);
    setFormError(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!draft) return;
    setFormError(null);
    const id = draft.id.trim();
    const name = draft.name.trim();
    const filename = draft.filename.trim();
    if (!id) {
      setFormError('Хэлний код оруулна уу.');
      return;
    }
    if (draft.mode === 'create' && !isBookLanguageCode(id)) {
      setFormError('Зөвшөөрөгдсөн хэлний кодоос сонгоно уу.');
      return;
    }
    if (!name) {
      setFormError('Нэр оруулна уу.');
      return;
    }
    if (draft.mode === 'create' && existingLanguageIds.has(id)) {
      setFormError(BOOK_LANGUAGE_DUPLICATE_CODE_MESSAGE);
      return;
    }
    setSaving(true);
    try {
      if (draft.mode === 'create') {
        await createBookLanguage({ id, name, filename, active: draft.active });
      } else {
        await updateBookLanguage({ id, name, filename, active: draft.active });
      }
      closeForm();
      await load({ silent: true });
    } catch (err: unknown) {
      setFormError(
        settingsListApiError(err, draft.mode === 'create' ? 'Нэмэхэд алдаа гарлаа.' : 'Шинэчлэхэд алдаа гарлаа.'),
      );
    } finally {
      setSaving(false);
    }
  };

  const dialogOpen = draft !== null;
  const dialogTitle = draft?.mode === 'create' ? 'Шинэ хэл' : draft?.mode === 'edit' ? 'Хэл засах' : '';

  return (
    <>
      <SettingsCrudDialog
        open={dialogOpen}
        title={dialogTitle}
        error={formError}
        saving={saving}
        onClose={closeForm}
        onSubmit={(e) => void handleSubmit(e)}
      >
        {draft && (
          <BookLanguageListForm draft={draft} onChange={setDraft} existingIds={existingLanguageIds} />
        )}
      </SettingsCrudDialog>

      <SettingsListSection
        title="Хэл"
        subtitle="Номын хэлний жагсаалт"
        onAdd={openCreate}
        error={loading ? null : error}
        loading={loading}
        skeletonColumns={7}
      >
        <TableContainer sx={{ maxHeight: { xs: 400, md: 560 } }}>
          <Table size="small" stickyHeader aria-label="Хэлний жагсаалт">
            <TableHead>
              <TableRow>
                {/* <TableCell sx={headSx}>ID</TableCell> */}
                <TableCell sx={headSx}>Код</TableCell>
                <TableCell sx={headSx}>Нэр</TableCell>
                <TableCell sx={headSx}>Зураг</TableCell>
                <TableCell sx={headSx}>Идэвхтэй</TableCell>
                <TableCell sx={headSx}>Шинэчилсэн</TableCell>
                <TableCell sx={{ ...headSx, width: 1, whiteSpace: 'nowrap' }} align="right">
                  Үйлдэл
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <SettingsEmptyTableRow colSpan={7} />
              ) : (
                rows.map((row) => {
                  const isActive = bookLanguageRowIsActive(row);
                  return (
                    <TableRow key={row.id} hover>
                      {/* <TableCell>{row.id}</TableCell> */}
                      <TableCell>{row.code}</TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>
                        <BookLanguageFlagCell row={row} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={isActive ? 'Тийм' : 'Үгүй'}
                          color={isActive ? 'success' : 'default'}
                          variant={isActive ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>{formatSettingsRowDate(row.updated_at)}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0} justifyContent="flex-end">
                          <SettingsListEditButton onEdit={() => openEdit(row)} />
                          <SettingsListDeleteButton
                            id={row.id}
                            deletingId={deletingId}
                            onDelete={deleteById}
                            itemLabel={`${row.name} (${row.code})`}
                          />
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </SettingsListSection>
    </>
  );
}
